import { HintTooltip } from '@/components/HintTooltip';
import { Tag } from '@/components/matching/matchingTags';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useRowClick } from '@/lib/navigation';
import {
    allBrandsState,
    equipmentForModelState,
    equipmentTypeFamiliesState,
    homeState,
    modelState,
    userState,
} from '@/state';
import { useAsyncAtomValue, useLoadableAtomValue } from '@/state/loadableHooks';
import { type JoinFacts, modelJoinFacts } from '@rock/models';
import type { HomeId, ModelId } from '@rock/types';
import { isTestId } from '@rock/util';
import { Loader2 } from 'lucide-react';
import { type FC, useMemo } from 'react';
import { EquipmentCard } from './EquipmentCard';
import { brandsById } from './modelBrand';
import { modelJoinBrand, useUnitBrands } from './modelJoin';

export const ModelEquipmentTab: FC<{ modelId: ModelId }> = ({ modelId }) => {
    // The tab badge shows the true total (server aggregation); this list is a bounded sample of it.
    const [sample, loadable] = useLoadableAtomValue(
        useMemo(() => equipmentForModelState(modelId), [modelId]),
        [],
    );
    // A recall fixture's model is joined by every E2E run; those units are not the model's owners.
    const equipment = useMemo(() => sample.filter((item) => !isTestId(item.homeId)), [sample]);
    const model = useAsyncAtomValue(useMemo(() => modelState(modelId), [modelId]));
    const brands = useAsyncAtomValue(allBrandsState, []);
    const brandLookup = useMemo(() => brandsById(brands), [brands]);
    const families = useAsyncAtomValue(equipmentTypeFamiliesState, []);
    const unitBrands = useUnitBrands(equipment, brandLookup);
    const modelBrand = model ? modelJoinBrand(model, brandLookup) : undefined;

    return (
        <Card>
            <CardHeader>
                <CardTitle>Equipment</CardTitle>
                <CardDescription>
                    The first 10 pieces of equipment identified as this model, across all homes, each judged by the
                    join's type and brand gates as it stands today.
                </CardDescription>
            </CardHeader>
            <CardContent>
                {loadable.state === 'hasError' ? (
                    <div className="text-sm text-destructive">Unable to load equipment for this model.</div>
                ) : loadable.state === 'loading' ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading equipment…
                    </div>
                ) : equipment.length === 0 ? (
                    <div className="section-description">No equipment has been identified as this model yet.</div>
                ) : (
                    <div className="grid gap-4">
                        {equipment.map((item) => (
                            <EquipmentCard
                                key={item.id}
                                equipment={item}
                                headerRight={
                                    <div className="flex flex-col items-end gap-1.5">
                                        <HomeOwnerLink homeId={item.homeId} />
                                        {model && (
                                            <UnitJoinTags
                                                facts={modelJoinFacts(
                                                    { model, brand: modelBrand },
                                                    { typeId: item.typeId, brand: unitBrands.get(item.id)?.join },
                                                    families,
                                                )}
                                                unitBrand={unitBrands.get(item.id)?.label}
                                            />
                                        )}
                                    </div>
                                }
                            />
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

/** What the join would flag about this unit today; nothing when its type and brand agree with the model's. */
const UnitJoinTags: FC<{ facts: JoinFacts; unitBrand: string | undefined }> = ({ facts, unitBrand }) => (
    <span className="flex flex-wrap justify-end gap-1">
        {facts.affinity === 'family' && (
            <HintTooltip hint="The unit's type is another type in this model's type family. It stays joined, flagged in case the filings aren't one product.">
                <Tag className="bg-amber-200 text-amber-900">joined via family</Tag>
            </HintTooltip>
        )}
        {!facts.affinity && (
            <HintTooltip hint="The unit's type is unrelated to this model's, so the join would no longer put it here. Refresh Model Specs moves it to a model of its own type.">
                <Tag className="bg-rose-200 text-rose-900">other type</Tag>
            </HintTooltip>
        )}
        {facts.brand === 'contradicts' && (
            <HintTooltip hint="The owner gave a different brand than this model's, and the owner's brand is the truth. Refresh Model Specs moves the unit to a model of its own brand, or a provisional one if the catalog doesn't have the brand yet.">
                <Tag className="bg-rose-200 text-rose-900">names another brand: {unitBrand}</Tag>
            </HintTooltip>
        )}
    </span>
);

/** The home + owner this equipment belongs to, click-through (cmd+click aware) to the home's equipment tab. */
const HomeOwnerLink: FC<{ homeId: HomeId }> = ({ homeId }) => {
    const home = useAsyncAtomValue(useMemo(() => homeState(homeId), [homeId]));
    const owner = useAsyncAtomValue(useMemo(() => userState(home?.owner), [home?.owner]));
    const onRowClick = useRowClick();

    return (
        <button
            type="button"
            onClick={onRowClick(`/directory/homes/${homeId}/equipment`)}
            className="flex max-w-[12rem] flex-col items-end text-right"
        >
            <span className="truncate text-sm font-medium text-primary hover:underline">{home?.name ?? homeId}</span>
            <span className="truncate text-xs text-muted-foreground">{owner?.email ?? owner?.name ?? '—'}</span>
        </button>
    );
};
